import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Chip } from '@/components/Chip'
import { GridTable } from '@/components/GridTable'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { formatJobTime } from '@/screens/shared/jobFormatting'
import { useActiveOrgId } from '@/data/store'
import { MInvite } from '@/screens/m-invite/MInvite'
import { MMfareset } from '@/screens/m-mfareset/MMfareset'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type OrganisationUser = components['schemas']['OrganisationUser']
type OrgRole = components['schemas']['OrgRole']
type WorkspaceRole = components['schemas']['WorkspaceRole']
type WorkspaceSummary = components['schemas']['WorkspaceSummary']
type Invitation = components['schemas']['Invitation']
type CapabilityMatrix = components['schemas']['CapabilityMatrix']

const ORG_ROLES: OrgRole[] = ['OWNER', 'ADMIN', 'OPERATOR', 'AUDITOR']
const ORG_ROLE_CHIP: Record<OrgRole, 'type' | 'pending' | 'off'> = { OWNER: 'type', ADMIN: 'pending', OPERATOR: 'off', AUDITOR: 'off' }

/**
 * `v-users` — process/09-SCREEN-SPECS/v-users.md. Members, the capability matrix (**fetched, never
 * embedded** — rule 1), pending invitations. *Invite user* opens `m-invite` (W144); the per-row
 * *reset factor* menu item opens `m-mfareset` (W144) — their first real callers. `user.changeRole`
 * fires from the per-row role **select**, never a button (the spec's own "Fired without a
 * control" table); `user.deactivate`/`reactivate`/`resetFactor`/`revokeGrants` all fire from the
 * per-row menu. The grants "Review" panel is new this pass — no modal unit exists for it, so it is
 * built as an inline expandable panel rather than a sixth catalogued modal.
 *
 * **`avatar`/`grid-tools`/`grid-footer`/`link` added W160.** `Avatar` (new, `src/components/`) is
 * this build's first real `avatar` — two-letter initials derived from `display_name`/`email`, the
 * same purely presentational derivation the prototype's own fixture rows use, never a served or
 * inferred business value. `grid-tools` carries a real "Refresh" `icon-button`; both `grid-footer`s
 * are informational notes on data this screen already reads (`Invitation.expires_at` exists on the
 * schema, so "invitations expire on their own schedule" is a true statement, not a formatted
 * `MachineTime`); "Review" is now `data-cmp="link"`, a real, already-wired control.
 *
 * **The pending-invitation role chip recoloured (W172)** — the prototype's own member table draws
 * a role-specific chip per row (`Owner` → `type`, `Admin` → `st-pending`, `Operator`/`Auditor` →
 * `st-off`), not one flat grey for every role. **`factor_enrolled` left unchanged**: the
 * prototype's own "Enrolled" state renders as coloured TEXT (`color:var(--pill-ok)`), never a
 * chip at all — this component's choice to render it as a chip has no prototype colour to match
 * against, a different and larger question than the one this pass answers.
 */
export function VUsers() {
  const orgId = useActiveOrgId()!
  const queryClient = useQueryClient()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [resettingUser, setResettingUser] = useState<OrganisationUser | undefined>()
  const [reviewingUserId, setReviewingUserId] = useState<string | undefined>()

  const users = useQuery({
    queryKey: ['org-users', orgId],
    // Found live, 2026-09-25 — this exact endpoint, along with capability-matrix and invitations
    // below, fired with a literal, unsubstituted `{org_id}` in the URL. Same mount-time race as
    // VHealth.tsx/VDrift.tsx's own fix: `ShellLayer`'s `setActiveOrg` effect (`data/store.ts`) runs
    // after the first render, so a screen that mounts as part of the shell's initial commit (a page
    // reload/direct navigation landing here) can read a still-null `orgId` on its own first render.
    enabled: Boolean(orgId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/users', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly OrganisationUser[]
    },
  })

  const matrix = useQuery({
    queryKey: ['capability-matrix', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<CapabilityMatrix> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/reference/capability-matrix', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as CapabilityMatrix
    },
  })

  const invitations = useQuery({
    queryKey: ['invitations', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/invitations', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly Invitation[]
    },
  })

  const workspaces = useQuery({
    queryKey: ['workspaces', orgId],
    enabled: inviteOpen,
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly WorkspaceSummary[]
    },
  })

  const changeRole = useMutation({
    mutationFn: async ({ userId, orgRole }: { userId: string; orgRole: OrgRole }) => {
      const client = getApiClient()
      const response = await client.PATCH('/orgs/{org_id}/users/{user_id}', { params: { path: { org_id: orgId, user_id: userId } }, body: { org_role: orgRole } })
      return unwrap(response as { data?: OrganisationUser; error?: Problem })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-users', orgId] }),
  })

  const deactivate = useMutation({
    mutationFn: async (userId: string) => {
      const client = getApiClient()
      const { error } = await client.POST('/orgs/{org_id}/users/{user_id}/deactivate', { params: { path: { org_id: orgId, user_id: userId } } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-users', orgId] }),
  })

  const reactivate = useMutation({
    mutationFn: async (userId: string) => {
      const client = getApiClient()
      const { error } = await client.POST('/orgs/{org_id}/users/{user_id}/reactivate', { params: { path: { org_id: orgId, user_id: userId } } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-users', orgId] }),
  })

  const revokeGrants = useMutation({
    mutationFn: async (userId: string) => {
      const client = getApiClient()
      const { error } = await client.DELETE('/orgs/{org_id}/users/{user_id}/grants', { params: { path: { org_id: orgId, user_id: userId } } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-users', orgId] }),
  })

  const revokeInvitation = useMutation({
    mutationFn: async (invitationId: string) => {
      const client = getApiClient()
      const { error } = await client.DELETE('/orgs/{org_id}/invitations/{invitation_id}', { params: { path: { org_id: orgId, invitation_id: invitationId } } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invitations', orgId] }),
  })

  const problem = users.error instanceof ApiProblemError ? users.error.problem : undefined

  // OI-C94(b)/(c): `organisationListInvitations` returns every invitation ever issued — LIVE,
  // ACCEPTED, REVOKED and EXPIRED alike (its own description says "newest first", not "pending
  // only") — and this panel's own heading promises only the pending ones. Revoking or re-inviting
  // already invalidates and refetches this query correctly; what was missing is this filter, so a
  // REVOKED row kept rendering identically to a LIVE one forever, looking like a revoke that never
  // took effect, and a re-invite looked like two simultaneously-live rows for one address.
  const pendingInvitations = (invitations.data ?? []).filter((inv) => inv.state === 'LIVE')

  return (
    <div data-scr={unitId('v-users')}>
      <div data-cmp="panel-header" className="flex items-center gap-5 mb-6">
        <h1 className="m-0 text-section font-semibold flex-1">Users</h1>
        <div data-cmp="grid-tools" className="flex items-center gap-4">
          <IconButton
            data-act={undefined}
            icon="i-refresh"
            label="Refresh"
            disabled={users.isFetching}
            onClick={() => queryClient.invalidateQueries({ queryKey: ['org-users', orgId] })}
          />
          <Button data-act={undefined} onClick={() => setInviteOpen(true)}>
            Invite user
          </Button>
        </div>
      </div>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-8">
        <GridTable
          columns={[
            {
              key: 'member',
              header: 'Member',
              render: (u) => (
                <>
                  <Avatar name={u.display_name ?? u.email} />
                  {u.display_name ?? u.email}
                  <div className="text-small text-muted">{u.email}</div>
                  {u.sole_privileged_human && <Note>The sole privileged human in this organisation.</Note>}
                </>
              ),
            },
            {
              key: 'role',
              header: 'Role',
              stopRowClick: true,
              render: (u) => (
                <select
                  data-act={actionId('user.changeRole')}
                  value={u.org_role}
                  disabled={u.deactivated}
                  onChange={(e) => changeRole.mutate({ userId: u.user_id, orgRole: e.target.value as OrgRole })}
                  className="border border-bd2 rounded-1 py-2 px-3"
                >
                  {ORG_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              ),
            },
            {
              key: 'reach',
              header: 'Reach',
              stopRowClick: true,
              render: (u) => {
                const grantCount = u.grants?.length ?? 0
                const reachLabel = u.reach === 'ALL_WORKSPACES' ? 'Every workspace' : grantCount === 0 ? 'No access' : `${grantCount} workspace${grantCount === 1 ? '' : 's'}`
                return (
                  <>
                    {reachLabel}
                    <div>
                      <button
                        type="button"
                        data-cmp="link"
                        className="underline text-link bg-transparent border-0 p-0 cursor-pointer text-small"
                        onClick={() => setReviewingUserId(reviewingUserId === u.user_id ? undefined : u.user_id)}
                      >
                        Review
                      </button>
                    </div>
                  </>
                )
              },
            },
            { key: 'twoFactor', header: 'Two-factor', render: (u) => <Chip variant={u.factor_enrolled ? 'default' : 'off'}>{u.factor_enrolled ? 'Enrolled' : 'Not enrolled'}</Chip> },
            { key: 'lastSignIn', header: 'Last sign-in', render: (u) => (u.last_sign_in_at ? formatJobTime(u.last_sign_in_at) : 'Never signed in') },
            {
              key: 'actions',
              header: '',
              stopRowClick: true,
              render: (u) => (
                <div className="flex gap-2">
                  {u.deactivated ? (
                    <Button
                      data-act={actionId('user.reactivate')}
                      ghost
                      disabled={reactivate.isPending && reactivate.variables === u.user_id}
                      onClick={() => reactivate.mutate(u.user_id)}
                    >
                      Reactivate
                    </Button>
                  ) : (
                    <Button
                      data-act={actionId('user.deactivate')}
                      ghost
                      disabled={deactivate.isPending && deactivate.variables === u.user_id}
                      onClick={() => deactivate.mutate(u.user_id)}
                    >
                      Deactivate
                    </Button>
                  )}
                  <Button data-act={undefined} ghost onClick={() => setResettingUser(u)}>
                    Reset factor
                  </Button>
                  <Button
                    data-act={actionId('user.revokeGrants')}
                    ghost
                    disabled={revokeGrants.isPending && revokeGrants.variables === u.user_id}
                    onClick={() => revokeGrants.mutate(u.user_id)}
                  >
                    Revoke grants
                  </Button>
                </div>
              ),
            },
          ]}
          rows={users.data ?? []}
          getRowKey={(u) => u.user_id}
          rowClassName={(u) => (u.deactivated ? 'bg-row' : '')}
          renderExpansion={(u) => (reviewingUserId === u.user_id ? <ReviewGrantsPanel userId={u.user_id} /> : null)}
        />
        <div data-cmp="grid-footer" className="p-4 border-t border-bd">
          <Note>Every member of the organisation — not paged, not filtered.</Note>
        </div>
      </div>

      {matrix.data && matrix.data.roles && matrix.data.rows && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6 mb-8">
          <div data-cmp="panel-header" className="mb-4 font-semibold text-body-lg">
            What each role means
          </div>
          <GridTable
            columns={[
              { key: 'capability', header: 'Capability', render: (row) => row.label },
              ...matrix.data.roles.map((r) => ({ key: r, header: r, render: (row: (typeof matrix.data)['rows'][number]) => (row.roles.includes(r) ? '✓' : '') })),
            ]}
            rows={matrix.data.rows}
            getRowKey={(row) => row.label}
          />
          <div data-cmp="grid-footer" className="border-t border-bd pt-4">
            <Note>
              Permissions have two dimensions. <b>Owner and Admin reach every workspace in the organisation</b> and that is a fixed
              property, not a setting. <b>Operator and Auditor are scoped by grants</b>, shown in the reach column. Every restore
              requires a typed confirmation of the destination at the moment you run it, regardless of role — and the confirmation is
              bound to a hash of the restore's parameters, so editing any input invalidates it.
            </Note>
          </div>
        </div>
      )}

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6">
        <div data-cmp="panel-header" className="mb-4 font-semibold text-body-lg">
          Pending invitations
        </div>
        {pendingInvitations.length === 0 && <Note>No pending invitations.</Note>}
        {pendingInvitations.map((inv) => (
          <div key={inv.invitation_id} className="flex items-center gap-4 py-3 border-b border-bd last:border-b-0">
            <span className="flex-1">{inv.email}</span>
            <Chip variant={ORG_ROLE_CHIP[inv.org_role] ?? 'plain'}>{inv.org_role}</Chip>
            <Button
              data-act={actionId('invitation.revoke')}
              ghost
              disabled={revokeInvitation.isPending && revokeInvitation.variables === inv.invitation_id}
              onClick={() => revokeInvitation.mutate(inv.invitation_id)}
            >
              Revoke
            </Button>
          </div>
        ))}
        <div data-cmp="grid-footer" className="border-t border-bd pt-4">
          <Note>Invitations expire on their own served schedule; none is cancelled from this view except by revoking it.</Note>
        </div>
      </div>

      {inviteOpen && (
        <MInvite
          orgId={orgId}
          workspaces={workspaces.data ?? []}
          onDismiss={() => setInviteOpen(false)}
          onInvited={() => {
            setInviteOpen(false)
            queryClient.invalidateQueries({ queryKey: ['invitations', orgId] })
          }}
        />
      )}

      {resettingUser && (
        <MMfareset
          orgId={orgId}
          userId={resettingUser.user_id}
          userName={resettingUser.display_name ?? resettingUser.email}
          userEmail={resettingUser.email}
          onDismiss={() => setResettingUser(undefined)}
          onReset={() => {
            setResettingUser(undefined)
            queryClient.invalidateQueries({ queryKey: ['org-users', orgId] })
          }}
        />
      )}
    </div>
  )
}

function ReviewGrantsPanel({ userId }: { userId: string }) {
  const orgId = useActiveOrgId()!
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Record<string, WorkspaceRole | undefined> | undefined>(undefined)

  const workspaces = useQuery({
    queryKey: ['workspaces', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly WorkspaceSummary[]
    },
  })

  const grants = useQuery({
    queryKey: ['user-grants', orgId, userId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<{ reach: 'ALL_WORKSPACES' | 'GRANTED_ONLY'; items: readonly components['schemas']['WorkspaceGrant'][] }> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/users/{user_id}/grants', { params: { path: { org_id: orgId, user_id: userId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as { reach: 'ALL_WORKSPACES' | 'GRANTED_ONLY'; items: readonly components['schemas']['WorkspaceGrant'][] }
    },
  })

  const saved: Record<string, WorkspaceRole | undefined> = Object.fromEntries((grants.data?.items ?? []).map((g) => [g.workspace_id, g.workspace_role]))
  const effective = draft ?? saved

  const save = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const items = Object.entries(effective)
        .filter((entry): entry is [string, WorkspaceRole] => Boolean(entry[1]))
        .map(([workspace_id, workspace_role]) => ({ workspace_id, workspace_role }))
      const response = await client.PUT('/orgs/{org_id}/users/{user_id}/grants', { params: { path: { org_id: orgId, user_id: userId } }, body: { items } })
      return unwrap(response as { data?: unknown; error?: Problem })
    },
    onSuccess: () => {
      setDraft(undefined)
      queryClient.invalidateQueries({ queryKey: ['user-grants', orgId, userId] })
      queryClient.invalidateQueries({ queryKey: ['org-users', orgId] })
    },
  })

  if (grants.data?.reach === 'ALL_WORKSPACES') {
    return (
      <div className="p-6 bg-row">
        <Note>This member's reach is every workspace, as a property of their role — not restrictable by a grant.</Note>
      </div>
    )
  }

  return (
    <div className="p-6 bg-row">
      {(workspaces.data ?? []).map((w) => (
        <label key={w.workspace_id} className="flex items-center gap-3 py-2">
          <input
            type="checkbox"
            checked={Boolean(effective[w.workspace_id])}
            onChange={(e) => setDraft({ ...effective, [w.workspace_id]: e.target.checked ? 'OPERATOR' : undefined })}
          />
          {w.name}
          {effective[w.workspace_id] && (
            <select
              value={effective[w.workspace_id]}
              onChange={(e) => setDraft({ ...effective, [w.workspace_id]: e.target.value as WorkspaceRole })}
              className="border border-bd2 rounded-1 py-1 px-2 text-small"
            >
              <option value="ADMIN">Admin</option>
              <option value="OPERATOR">Operator</option>
              <option value="VIEWER">Viewer</option>
            </select>
          )}
        </label>
      ))}
      <Button data-act={actionId('user.replaceGrants')} disabled={!draft || save.isPending} onClick={() => save.mutate()}>
        Save grants
      </Button>
    </div>
  )
}
